import { useState } from "react";

import { formattedOnlyDate, isNotEmpty, subtractDays } from "@/libs/utils";

import { categoryList } from "@/consts/Bid";
import { PastBusinessDateRangeList } from "@/consts/PastBusiness";

import { BidCategory } from "@/types/bid";
import { PastBusinessFilter } from "@/types/pastbusiness";

import CommonInput from "@/components/Common/Input";
import CommonSelect from "@/components/Common/Select";
import CommonPlusButton from "@/components/Common/PlusButton";
import { DateRangePicker } from "@/components/DateRangePicker";
import CommonRadio from "@/components/Common/Radio";
import CommonCheckbox from "@/components/Common/Checkbox";
import CommonButton from "@/components/Common/Button";
import KeywordItem from "@/components/KeywordItem";

type Props = {
  filter: PastBusinessFilter;
  setFilter: React.Dispatch<React.SetStateAction<PastBusinessFilter>>;
  dateRange: string;
  setDateRange: React.Dispatch<React.SetStateAction<string>>;
  onSearch: () => void;
};
export default function PastBusinessSearch({
  filter,
  setFilter,
  dateRange,
  setDateRange,
  onSearch,
}: Props) {
  const [keyword, setKeyword] = useState<string>("");
  const [budgetNoLimit, setNoLimit] = useState<boolean>(true);
  const handleClickPlus = () => {
    if (isNotEmpty(keyword)) {
      setFilter((prev) => ({
        ...prev,
        keywords: prev.keywords.concat(keyword),
      }));
      setKeyword("");
    }
  };
  const handleDeleteItem = (index: number) => {
    setFilter((prev) => ({
      ...prev,
      keywords: prev.keywords.toSpliced(index, 1),
    }));
  };

  return (
    <>
      <div className="w-full min-w-[1100px] bg-white rounded-[12px] p-[20px] my-[20px] flex flex-col gap-[12px] shadow-[0_3.5px_5.5px_0_rgba(0,0,0,0.05)]">
        <div className="flex gap-[8px] items-center">
          <div className="w-[90px] text-sm text-gray-B font-semibold">
            공고 제목
          </div>
          <div className="flex gap-[8px]">
            <CommonInput
              placeholder="키워드를 입력해보세요"
              className="w-[200px]"
              defaultValue={keyword}
              onChange={(value) => {
                setKeyword(value);
              }}
              onKeyDownEnter={() => {
                handleClickPlus();
              }}
            />
            <CommonSelect
              useAll={false}
              defaultValue={filter.bool_op}
              optionList={[
                { label: "OR", value: "OR" },
                { label: "AND", value: "AND" },
              ]}
              onChange={(value: any) => {
                setFilter((prev) => ({ ...prev, bool_op: value }));
              }}
            />
            <CommonPlusButton onClick={handleClickPlus} />
            <div className="flex gap-[8px] overflow-x-auto">
              {filter.keywords.map((input, index) => (
                <KeywordItem
                  key={index}
                  index={index}
                  label={input}
                  onDelete={() => handleDeleteItem(index)}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-[8px] items-center">
          <div className="w-[90px] text-sm text-gray-B font-semibold">분류</div>
          <CommonSelect
            className="w-[125px]"
            defaultValue={filter.category}
            optionList={categoryList}
            onChange={(value) => {
              setFilter((prev) => ({
                ...prev,
                category: value as BidCategory,
              }));
            }}
          />
        </div>
        <div className="flex gap-[8px]">
          <div className="w-[80px] text-sm text-gray-B font-semibold">
            공고일
          </div>
          <div className="flex flex-col gap-[8px]">
            <DateRangePicker
              defaultValue={{
                from: new Date(filter.begin_dt),
                to: new Date(filter.end_dt),
              }}
              disabled={dateRange !== ""}
              onSelect={(e: any) => {
                setFilter((prev) => ({
                  ...prev,
                  begin_dt: formattedOnlyDate(e.from),
                  end_dt: formattedOnlyDate(e.to),
                }));
              }}
            />
            <div className="flex gap-[14px] px-2">
              {PastBusinessDateRangeList.map((date, index) => (
                <CommonRadio
                  key={index}
                  label={date.label}
                  name="date"
                  defaultValue={date.value}
                  checked={date.value === dateRange}
                  onChange={(value) => {
                    setDateRange(value);
                    const maxDate = new Date();
                    if (value !== "" && value !== "999") {
                      setFilter((prev) => ({
                        ...prev,
                        begin_dt: formattedOnlyDate(
                          subtractDays(maxDate, Number(value)).toString()
                        ),
                        end_dt: formattedOnlyDate(maxDate.toString()),
                      }));
                    }
                  }}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-between">
          <div className="flex gap-[8px] items-center">
            <div className="w-[90px] text-sm text-gray-B font-semibold">
              사업 금액
            </div>
            <CommonInput
              type="number"
              className="w-[125px]"
              defaultValue={filter.min_amount}
              onChange={(value) => {
                setFilter((prev) => ({
                  ...prev,
                  min_amount: value,
                }));
              }}
              disabled={budgetNoLimit}
            />
            <div className="px-[15px] text-[10px] text-gray-B font-semibold">
              ~
            </div>
            <div className="w-[130px]">
              {!budgetNoLimit && (
                <CommonInput
                  type="number"
                  className="w-[125px]"
                  defaultValue={filter.max_amount}
                  onChange={(value) => {
                    setFilter((prev) => ({
                      ...prev,
                      max_amount: value,
                    }));
                  }}
                />
              )}
            </div>
            <CommonCheckbox
              size="sm"
              label="금액 제한 없음"
              defaultValue={budgetNoLimit}
              onChange={(value) => {
                setNoLimit(value);
              }}
            />
          </div>
          <CommonButton label="검색하기" onClick={onSearch} />
        </div>
      </div>
    </>
  );
}
